// Copyright (c) 2020 Comsearch, Inc., All rights reserved

import {
  castToFloat,
  castToString,
} from 'helpers/reduxFormFunctions'
import { makeModulationSchemeLabel } from 'redux/helpers'

// reduce modulations array to only the necessary data for details form
// and assign to the radio calculated values based on these modulations' configurations.
// (bandwidth, coordinatedPower, engineeringReference, maxPower, modelNumber)
const reduceModulations = mods => (
  mods.reduce((a, d) => {
    const {
      emissionDesignator,
      enabled,
      modelNumber,
      modulationScheme,
      rxThreshold,
      throughput,
    } = d

    const attributes = d.attributes || []
    const bandwidth = castToString(d.bandwidth)
    // this is used for display to the user in the Path Details - Radio Section - modulations details modal
    const modulationSchemeDescription = makeModulationSchemeLabel(modulationScheme)
    // this is used for display to the user in the Path Details - Radio Section - modulations table
    const modulationSchemeLabel = makeModulationSchemeLabel(modulationScheme)

    // the API will tell us what power to apply here. we don't need to worry if it's a regular ole Radio or a Favorite Radio.
    const power = castToString(d.enabledPower)

    const radioModulationId = castToString(d.radioModulationId)
    const txPowerMax = castToString(d.txPowerMax)
    const txPowerMin = castToString(d.txPowerMin)

    // maxAtpcTrigger and minAtpcTrigger are used when validating ATPC Trigger Level
    // but don't have an associated redux-form <Field />. because of this, we don't
    // need to convert it to string when sending to the redux-form <form />. the below
    // assumes that a modulation will always have a rxThreshold as a fallback for minAtpcTrigger
    const maxAtpcTrigger = d.maxAtpcTrigger
    const minAtpcTrigger = d.minAtpcTrigger || rxThreshold

    // push this modulation to the array no matter what
    a.modulations.push({
      attributes,
      bandwidth,
      emissionDesignator,
      enabled,
      maxAtpcTrigger,
      minAtpcTrigger,
      modelNumber,
      modulationSchemeDescription,
      modulationSchemeLabel,
      power,
      radioModulationId,
      rxThreshold,
      throughput,
      txPowerMax,
      txPowerMin,
    })

    // then do some checks for radio level settings

    // the first enabled modulation will be used to
    // define calculated values at the Radio's level
    // aka "First Modulation"
    if (!a.modelNumber && enabled) {
      a.modelNumber = modelNumber
      a.engineeringReference = radioModulationId
    }

    // bandwidth is shown as the greatest bandwidths of all enabled modulations.
    // this is probably but not necessarily the first one so we still need to check against all other modulations.
    if (
      enabled && (
        bandwidth > a.bandwidth ||
        a.bandwidth === undefined
      )
    ) {
      a.bandwidth = bandwidth
    }

    // capture max power based on the greatest power of the enabled modulations
    if (enabled && (a.maxPower === null || castToFloat(power) > castToFloat(a.maxPower))) {
      a.maxPower = power
    }

    return a
  }, { maxPower: null, modulations: [] })
)

export default reduceModulations
